import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { currencyCode } from '../utils/currency';

const EMPTY = {
  tag_id: '', breed: '', sex: 'ewe', date_of_birth: '', status: 'active', dam_id: '', litter_size: '',
  purchase_price: '', purchase_source: '', purchase_date: '', sale_price: '', market_value: '', sale_buyer: '',
  sale_date: '', death_date: '', death_reason: '', notes: '',
};
const STEPS = [
  { key: 'identity', label: 'Identity', hint: 'Animal details' },
  { key: 'origin', label: 'Origin', hint: 'Lineage & purchase' },
  { key: 'lifecycle', label: 'Lifecycle', hint: 'Status & value' },
];

const formFromSheep = sheep => ({
  ...EMPTY, ...sheep, dam_id: sheep.dam_id || '', litter_size: sheep.litter_size ?? '',
  purchase_price: sheep.purchase_price ?? '', sale_price: sheep.sale_price ?? '', market_value: sheep.market_value ?? '',
  death_date: sheep.death_date || '', death_reason: sheep.death_reason || '',
});

export default function SheepModal({ sheep, onClose, onSaved }) {
  const [form, setForm] = useState(sheep ? formFromSheep(sheep) : EMPTY);
  const [step, setStep] = useState(0);
  const [allSheep, setAllSheep] = useState([]);
  const [damSearch, setDamSearch] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const ccy = currencyCode();

  useEffect(() => {
    api.listSheep().then(data => setAllSheep(data.filter(item => !sheep || item.id !== sheep.id))).catch(e => setError(e.message));
  }, [sheep]);

  useEffect(() => {
    const onKey = event => event.key === 'Escape' && !saving && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const damOptions = useMemo(() => {
    const query = damSearch.trim().toLowerCase();
    return allSheep.filter(item => item.sex === 'ewe').filter(item => !query || item.tag_id.toLowerCase().includes(query) || (item.breed || '').toLowerCase().includes(query));
  }, [allSheep, damSearch]);

  const next = () => {
    setError('');
    if (step === 0 && !form.tag_id.trim()) return setError('Add a tag ID before continuing.');
    setStep(value => Math.min(value + 1, STEPS.length - 1));
  };

  const save = async event => {
    event.preventDefault();
    if (step < STEPS.length - 1) return next();
    if (!form.tag_id.trim()) { setStep(0); return setError('Tag ID is required.'); }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form, dam_id: form.dam_id ? parseInt(form.dam_id, 10) : null,
        litter_size: form.litter_size ? parseInt(form.litter_size, 10) : null,
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : null,
        sale_price: form.sale_price ? parseFloat(form.sale_price) : null,
        market_value: form.market_value ? parseFloat(form.market_value) : null,
      };
      onSaved(sheep ? await api.updateSheep(sheep.id, payload) : await api.createSheep(payload));
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={event => event.target === event.currentTarget && !saving && onClose()}>
      <form className="modal sheep-wizard" onSubmit={save}>
        <div className="modal-header">
          <div><span className="eyebrow">Flock record</span><h2>{sheep ? `Edit ${sheep.tag_id}` : 'Add a new animal'}</h2><p>Keep the record useful with a few clear details.</p></div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </div>

        <div className="wizard-steps" aria-label="Form progress">
          {STEPS.map((item, index) => (
            <button type="button" key={item.key} className={`${index === step ? 'active' : ''} ${index < step ? 'complete' : ''}`} onClick={() => index <= step && setStep(index)}>
              <span>{index < step ? '✓' : index + 1}</span><div><strong>{item.label}</strong><small>{item.hint}</small></div>
            </button>
          ))}
        </div>

        <div className="wizard-body">
          {step === 0 && <div className="wizard-panel">
            <div className="panel-intro"><h3>Identify the animal</h3><p>Start with what your team uses to recognise it in the flock.</p></div>
            <div className="form-grid form-grid-2">
              <div className="field"><label htmlFor="tag-id">Tag ID <span>*</span></label><input id="tag-id" autoFocus value={form.tag_id} onChange={e => set('tag_id', e.target.value)} placeholder="e.g. EWE-042" /></div>
              <div className="field"><label htmlFor="breed">Breed</label><input id="breed" value={form.breed} onChange={e => set('breed', e.target.value)} placeholder="e.g. Dorper" /></div>
              <div className="field"><label htmlFor="sex">Sex</label><select id="sex" value={form.sex} onChange={e => set('sex', e.target.value)}><option value="ewe">Ewe</option><option value="ram">Ram</option><option value="wether">Wether</option></select></div>
              <div className="field"><label htmlFor="dob">Date of birth</label><input id="dob" type="date" value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} /></div>
            </div>
            <div className="field"><label htmlFor="notes">Notes</label><textarea id="notes" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Temperament, markings, or anything the team should know…" /></div>
          </div>}

          {step === 1 && <div className="wizard-panel">
            <div className="panel-intro"><h3>Origin and lineage</h3><p>Link its mother and record acquisition details when they apply.</p></div>
            <div className="form-grid form-grid-2">
              <div className="field"><label htmlFor="dam-search">Dam (mother)</label><input id="dam-search" value={damSearch} onChange={e => setDamSearch(e.target.value)} placeholder="Filter by tag or breed" /><select value={form.dam_id} onChange={e => set('dam_id', e.target.value)}><option value="">Unknown / not recorded</option>{damOptions.map(item => <option key={item.id} value={item.id}>{item.tag_id}{item.breed ? ` · ${item.breed}` : ''}</option>)}</select></div>
              <div className="field"><label htmlFor="litter">Litter size at birth</label><input id="litter" type="number" min="1" max="10" value={form.litter_size} onChange={e => set('litter_size', e.target.value)} placeholder="e.g. 2" /></div>
            </div>
            <div className="subsection-heading"><span>Purchase details</span><small>Optional</small></div>
            <div className="form-grid form-grid-3">
              <div className="field"><label>Purchase date</label><input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} /></div>
              <div className="field"><label>Purchase price ({ccy})</label><input type="number" min="0" step="0.01" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} placeholder="0.00" /></div>
              <div className="field"><label>Source</label><input value={form.purchase_source} onChange={e => set('purchase_source', e.target.value)} placeholder="Breeder or farm" /></div>
            </div>
          </div>}

          {step === 2 && <div className="wizard-panel">
            <div className="panel-intro"><h3>Current lifecycle status</h3><p>Status controls which operational and financial details are relevant.</p></div>
            <div className="status-picker">
              {[['active', 'Active', 'In the flock'], ['sold', 'Sold', 'Left through sale'], ['deceased', 'Deceased', 'Mortality record']].map(([value, label, hint]) => <button type="button" key={value} className={form.status === value ? 'active' : ''} onClick={() => set('status', value)}><span className={`status-dot ${value}`} /><strong>{label}</strong><small>{hint}</small></button>)}
            </div>
            {form.status === 'active' && <div className="form-grid form-grid-2"><div className="field"><label>Current market value ({ccy})</label><input type="number" min="0" step="0.01" value={form.market_value} onChange={e => set('market_value', e.target.value)} placeholder="Expected sale value" /></div></div>}
            {form.status === 'sold' && <div className="form-grid form-grid-3"><div className="field"><label>Sale date</label><input type="date" value={form.sale_date} onChange={e => set('sale_date', e.target.value)} /></div><div className="field"><label>Sale price ({ccy})</label><input type="number" min="0" step="0.01" value={form.sale_price} onChange={e => set('sale_price', e.target.value)} placeholder="0.00" /></div><div className="field"><label>Buyer</label><input value={form.sale_buyer} onChange={e => set('sale_buyer', e.target.value)} placeholder="Buyer name" /></div></div>}
            {form.status === 'deceased' && <div className="form-grid form-grid-2"><div className="field"><label>Date of death</label><input type="date" value={form.death_date} onChange={e => set('death_date', e.target.value)} /></div><div className="field"><label>Cause or reason</label><input value={form.death_reason} onChange={e => set('death_reason', e.target.value)} placeholder="e.g. illness, predation" /></div></div>}
          </div>}
          {error && <div className="alert alert-danger mt-3" role="alert">{error}</div>}
        </div>

        <div className="wizard-footer">
          <button className="btn btn-ghost" type="button" onClick={step === 0 ? onClose : () => { setError(''); setStep(value => value - 1); }}>{step === 0 ? 'Cancel' : 'Back'}</button>
          <span>Step {step + 1} of {STEPS.length}</span>
          <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : step === STEPS.length - 1 ? (sheep ? 'Save changes' : 'Add to flock') : 'Continue'}</button>
        </div>
      </form>
    </div>
  );
}
